import React, { useEffect, useState } from "react";

import { Box, Chip, CircularProgress, Typography } from "@mui/material";

import CustomTable from "../../ui-components/customTable";
import AdminDBServices from "../../services/v1/adminDBServices";

const EMAIL_TYPE_LABELS = {
  daily_backup: "Daily Backup",
  payment_reminder: "Payment Reminder",
  self_service_otp: "Self-Service OTP",
  admin_magic_link: "Admin Update Link",
  date_change_otp: "Date-Change OTP",
  date_change_confirmation: "Date-Change Confirmation",
  reservation_confirmation: "Reservation Confirmation",
  welcome: "Welcome",
  password_reset: "Password Reset",
  general: "General",
};

// Latest daily_backup entry gets its own summary card at the top -- the
// same "system OK / UI OK" status the admins get by email, visible
// without digging through the full log.
const LatestBackupSummary = ({ entry }) => {
  if (!entry) return null;
  const meta = entry.metadata || {};

  const statusChip = (label, ok) => (
    <Chip
      label={`${label}: ${ok ? "Good" : "Needs attention"}`}
      color={ok ? "success" : "error"}
      variant="filled"
      size="small"
      sx={{ mr: 1, mb: 1 }}
    />
  );

  return (
    <Box sx={{ p: 2, border: "1px solid lightgrey", borderRadius: 2, mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
        Latest Daily Backup -- {new Date(entry.created_at).toLocaleString()}
      </Typography>
      <Chip
        label={entry.status === "sent" ? "Backup System: Running" : "Backup System: FAILED"}
        color={entry.status === "sent" ? "success" : "error"}
        size="small"
        sx={{ mr: 1, mb: 1 }}
      />
      {statusChip("System API", meta.api_ok)}
      {statusChip("System UI", meta.ui_ok)}
      {meta.row_count != null && (
        <Chip label={`${meta.row_count} rows backed up`} size="small" sx={{ mb: 1 }} />
      )}
      {entry.error_message && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {entry.error_message}
        </Typography>
      )}
    </Box>
  );
};

const SystemHealth = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLog = async () => {
      try {
        const response = await AdminDBServices.getEmailLog();
        setRows(response.data?.data || []);
      } catch (err) {
        setError(err.message || "Failed to load email log.");
      } finally {
        setLoading(false);
      }
    };
    fetchLog();
  }, []);

  const latestBackup = rows.find((row) => row.email_type === "daily_backup");

  const columns = [
    {
      label: "Sent At",
      field: "created_at",
      renderCell: (row) => new Date(row.created_at).toLocaleString(),
    },
    {
      label: "Type",
      field: "email_type",
      renderCell: (row) => EMAIL_TYPE_LABELS[row.email_type] || row.email_type,
    },
    { label: "Recipients", field: "recipients" },
    {
      label: "Status",
      field: "status",
      renderCell: (row) => (
        <Chip
          label={row.status === "sent" ? "Sent" : "Failed"}
          color={row.status === "sent" ? "success" : "error"}
          size="small"
        />
      ),
    },
    {
      label: "Details",
      field: "error_message",
      renderCell: (row) => row.error_message || "-",
    },
  ];

  if (loading) {
    return (
      <Box align="center">
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body1" color="error">
        {error}
      </Typography>
    );
  }

  return (
    <Box>
      <LatestBackupSummary entry={latestBackup} />
      {rows.length > 0 ? (
        <CustomTable columns={columns} rows={rows} />
      ) : (
        <Typography variant="body1" color="textSecondary">
          No emails logged yet.
        </Typography>
      )}
    </Box>
  );
};

export default SystemHealth;
